<script>
    // 허브 격자의 '사전 테스트' / '사후 점검' 자리 하나(저작 화면). HubChatPicker 가 한 카드를 반으로 갈라
    // 왼쪽=사전, 오른쪽=사후로 하나씩 놓는다.
    //   비었으면 → ＋ 자리. 누르면 **그 용도로 지정된 챗만** 골라 담는다.
    //              사후 자리에 담긴 사전 테스트가 있으면 [사전 테스트로 만들기]가 먼저다(createFrom).
    //   찼으면  → 그 챗 카드(썸네일·제목·[채팅 빌더]·카드 설정).
    //
    // ⚠️ 여기서 챗의 용도를 바꾸지 않는다. 용도는 챗이 갖고(CHATS.PURPOSE) 챗 빌더 [블록] 탭에서 만들 것까지
    //    바꾸는 값이라, 허브에서 몰래 바꾸면 그 챗의 저작 화면이 통째로 달라진다. 후보가 없으면 어디서
    //    바꾸는지만 알려 준다 — 바꾸고 돌아오면 부모의 refreshChatInfo 가 후보를 다시 받아 온다.
    // ⚠️ 데이터(items 의 객체)는 부모 배열의 것을 그대로 받아 **제자리에서 고친다** — 고칠 때마다 'change'.
    import { createEventDispatcher } from "svelte";
    import { Button, Badge, Input } from "../../lib/ui";
    import HubCardIconField from "./HubCardIconField.svelte";

    export let role;              // "intake" | "exit"
    export let items = [];        // 이 용도로 담긴 카드(보통 한 장. 예전 저작이면 여럿일 수 있다)
    export let eligible = [];     // 이 용도인데 아직 안 담긴 챗 후보
    // 사후 자리 전용 — 담긴 사전 테스트({chatId,title}). 있으면 빈 자리에 [사전 테스트로 만들기]를 낸다.
    // ⚠️ 만들기는 부모가 한다(서버 HubSetup.createExitFromIntake → 격자에 담기). 여긴 'create' 만 올린다.
    export let createFrom = null;
    export let busy = false;

    const dispatch = createEventDispatcher();
    // 이름·설명은 챗 빌더 [대화 설정] 용도 선택지와 같은 말로 쓴다
    const META = {
        intake: { name: "사전 테스트", hint: "본 콘텐츠 전에 수준을 확인" },
        exit: { name: "사후 테스트", hint: "다 마친 뒤 다시 확인" },
    };
    $: meta = META[role];

    let picking = false;
    const pick = (c) => {
        picking = false;
        dispatch("add", c);
    };
</script>

{#if items.length > 0}
    <div class="tw-space-y-2.5">
        {#each items as p (p.chatId)}
            <div class="tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-card tw-shadow-sm">
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-pl-3 tw-pr-1.5 tw-pt-1.5">
                    <span class="tw-text-xs tw-font-semibold tw-text-primary">{meta.name}</span>
                    <button type="button" class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground hover:tw-bg-destructive/10 hover:tw-text-destructive"
                            title="격자에서 빼기" aria-label="격자에서 빼기" on:click={() => dispatch("remove", p.chatId)}>
                        <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                    </button>
                </div>
                <div class="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-pb-3 tw-pt-1">
                    {#if p.thumb}
                        <img class="tw-h-11 tw-w-11 tw-shrink-0 tw-rounded-md tw-object-cover" src={p.thumb} alt="" loading="lazy" />
                    {:else}
                        <div class="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-muted tw-text-sm tw-font-semibold tw-text-muted-foreground">
                            {(p.title || "?").slice(0, 1)}
                        </div>
                    {/if}
                    <div class="tw-min-w-0 tw-flex-1">
                        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                            <span class="tw-max-w-full tw-truncate tw-text-sm tw-font-semibold tw-text-foreground">{p.title}</span>
                            {#if !p.published}<Badge variant="required">미게시</Badge>{/if}
                        </div>
                        {#if p.subTitle}
                            <p class="tw-mb-0 tw-mt-0.5 tw-truncate tw-text-xs tw-text-muted-foreground">{p.subTitle}</p>
                        {/if}
                    </div>
                </div>
                <div class="tw-px-3 tw-pb-3">
                    <Button variant="outline" size="sm" class="tw-w-full" on:click={() => dispatch("open", p.chatId)}
                            title="이 채팅의 빌더를 새 탭에서 엽니다">
                        채팅 빌더
                        <svg class="tw-h-3.5 tw-w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M15 3h6v6" /><path d="M10 14 21 3" />
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        </svg>
                    </Button>
                </div>
                <!-- 구분선은 1px div 로 — preflight off 라 tw-border-t 는 다른 변이 3px 로 그려진다 -->
                <div class="tw-h-px tw-bg-border"></div>
                <!-- 반쪽 폭이라 설정은 세로로 쌓는다. tw-mt-auto: 옆 반쪽과 높이가 달라도 설정 칸이 바닥에 붙는다. -->
                <div class="tw-mt-auto tw-space-y-3 tw-bg-muted/30 tw-px-3 tw-py-3">
                    <!-- 사전·사후 모두 격자 카드가 없다([시작하기] 버튼) — 그림은 **허브에서 연 테스트 채팅의 헤더**에 쓰인다.
                         저장 칸은 본 콘텐츠 카드와 같다(BOT_CHATS.THUMB_ASSET_ID). -->
                    <HubCardIconField usage="header" iconUrl={p.iconUrl} fallbackUrl={p.thumb}
                                      on:pick={() => dispatch("pickIcon", p.chatId)} on:clear={() => dispatch("clearIcon", p.chatId)} />
                    <label class="tw-mb-0 tw-block tw-space-y-1 tw-font-normal">
                        <span class="tw-block tw-text-xs tw-font-medium tw-text-muted-foreground">카드 이름</span>
                        <Input type="text" placeholder={p.title || "채팅 제목"}
                               value={p.label}
                               on:change={(e) => { p.label = e.target.value; dispatch("change"); }} />
                    </label>
                    <!-- 진행 표시는 두지 않는다 — 테스트는 레슨처럼 '얼마나 왔나'를 쌓는 챗이 아니라(판정 한 번),
                         막대·숫자가 늘 빈 채로 뜬다. 저장도 'none' 으로 한다(HubChatPicker save).
                         대신 학습자 화면에서 이 자리가 **언제·어떻게** 나오는지를 적는다. -->
                    <p class="tw-mb-0 tw-text-xs tw-text-muted-foreground">
                        {#if role === "intake"}
                            <!-- 사전 테스트는 격자 카드로 안 나온다(PROFILE HubLayout — 사전 단계에선 격자 대신 버튼).
                                 카드 이름은 사전이 여럿일 때 버튼 글자가 된다. -->
                            학습자 화면에선 카드 대신 <b>[시작하기]</b> 버튼으로 나옵니다. 마쳐야 본 콘텐츠가 열려요.
                        {:else}
                            <!-- 사후 단계는 사전과 같은 화면이다(격자를 감추고 [시작하기]만, 2026-10-20 결정 — PROFILE HubLayout) -->
                            본 콘텐츠를 다 마치거나 운영자가 열면 사전 테스트처럼 <b>[시작하기]</b> 버튼으로 나와요.
                        {/if}
                    </p>
                </div>
            </div>
        {/each}
    </div>
{:else if picking}
    <!-- 고르기: 이 용도로 지정된 챗만. 자리 크기를 그대로 써서 옆 반쪽이 흔들리지 않게 한다. -->
    <div class="tw-flex tw-h-full tw-min-h-[180px] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-primary/40 tw-bg-card tw-shadow-sm">
        <div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-pl-3 tw-pr-1.5 tw-pt-1.5">
            <span class="tw-text-xs tw-font-semibold tw-text-primary">{meta.name} 고르기</span>
            <button type="button" class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground hover:tw-bg-accent"
                    title="닫기" aria-label="닫기" on:click={() => (picking = false)}>
                <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            </button>
        </div>
        {#if eligible.length === 0}
            <p class="tw-mb-0 tw-flex-1 tw-px-3 tw-pb-3 tw-pt-1 tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
                용도가 <b>'{meta.name}'</b>인 채팅이 없습니다.
                쓸 채팅의 <b>[채팅 빌더] → [대화 설정]</b>에서 용도를 '{meta.name}'(으)로 바꾸고 돌아오면 여기 나타나요.
            </p>
        {:else}
            <div class="tw-max-h-[240px] tw-flex-1 tw-overflow-y-auto tw-px-1.5 tw-pb-1.5">
                {#each eligible as c (c.id)}
                    <button type="button" class="tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-md tw-px-1.5 tw-py-1.5 tw-text-left hover:tw-bg-accent"
                            on:click={() => pick(c)}>
                        {#if c.thumb}
                            <img class="tw-h-8 tw-w-8 tw-shrink-0 tw-rounded-md tw-object-cover" src={c.thumb} alt="" loading="lazy" />
                        {:else}
                            <span class="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-muted tw-text-xs tw-font-semibold tw-text-muted-foreground">
                                {(c.title || "?").slice(0, 1)}
                            </span>
                        {/if}
                        <span class="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-text-foreground">{c.title}</span>
                        {#if !c.published}<Badge variant="required">미게시</Badge>{/if}
                    </button>
                {/each}
            </div>
        {/if}
    </div>
{:else if createFrom}
    <!-- 사후 빈 자리 + 담긴 사전 테스트가 있다 → **사전 테스트로 만들기**가 기본 동선이다.
         같은 문항이어야 비교가 되므로 따로 만든 챗을 고르는 것보다 이쪽이 맞는 길이다(고르기는 보조로 남긴다).
         ⚠️ 버튼을 버튼 안에 넣을 수 없어 이 자리는 통째 버튼이 아니라 상자다. -->
    <div class="tw-flex tw-h-full tw-min-h-[180px] tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-primary/30 tw-bg-card/70 tw-px-3 tw-py-4 tw-text-center tw-text-primary">
        <span class="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary/10">
            <svg class="tw-h-5 tw-w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14" /><path d="M12 5v14" /></svg>
        </span>
        <span class="tw-text-sm tw-font-semibold">{meta.name} 추가</span>
        <span class="tw-text-xs tw-text-muted-foreground">
            '{createFrom.title}'와 <b>같은 문항</b>으로 만들어요. 사전 테스트를 고치면 함께 바뀝니다.
        </span>
        <Button size="sm" disabled={busy} on:click={() => dispatch("create", createFrom.chatId)}>
            {busy ? "만드는 중…" : "사전 테스트로 만들기"}
        </Button>
        <button type="button" class="tw-text-xs tw-text-muted-foreground tw-underline-offset-2 hover:tw-text-foreground hover:tw-underline"
                disabled={busy} on:click={() => (picking = true)}>
            만들어 둔 채팅 고르기
        </button>
    </div>
{:else}
    <!-- 빈 자리: ＋ 하나로 '여기 넣을 수 있다'를 말한다. 점선 = 아직 비어 있는 자리. -->
    <button type="button"
            class="tw-flex tw-h-full tw-min-h-[180px] tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-primary/30 tw-bg-card/70 tw-px-3 tw-text-primary tw-transition-colors hover:tw-border-primary/60 hover:tw-bg-card"
            on:click={() => (picking = true)}>
        <span class="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary/10">
            <svg class="tw-h-5 tw-w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14" /><path d="M12 5v14" /></svg>
        </span>
        <span class="tw-text-sm tw-font-semibold">{meta.name} 추가</span>
        <span class="tw-text-xs tw-text-muted-foreground">{meta.hint}</span>
    </button>
{/if}
